/**
 * What content theft is plausibly costing an account, per month.
 *
 * The overview used to print a fixed pair of numbers. This turns the same card
 * into arithmetic over two things the account actually holds: the audience band
 * on its profiles, and how much of its material is currently online.
 *
 * Two rules the numbers exist to keep. It is always a range, because a single
 * figure on that card reads as money already lost. And every step is derived —
 * a band picked from a dropdown becomes one representative number here, in one
 * place, so a screen cannot quietly disagree with a mail about the same account.
 */

/**
 * The follower count a band stands for, for arithmetic only. Never shown: the
 * account said "10K–50K" and printing 30,000 back at her would be a precision
 * nobody claimed.
 *
 * Every value of `FOLLOWER_RANGES` has a key here, and the two bands the
 * product has since split (1K–5K / 5K–10K out of 1K–10K, 1M–5M / 5M+ out of
 * 1M+) sit either side of the figure the wider band would have used. The
 * retired labels are kept as keys as well, because a profile saved before the
 * split still carries one.
 */
export const FOLLOWER_MIDPOINTS: Record<string, number> = {
  'Under 1K': 500,
  '1K–5K': 3_000,
  '5K–10K': 7_500,
  '1K–10K': 5_000,
  '10K–50K': 30_000,
  '50K–100K': 75_000,
  '100K–500K': 300_000,
  '500K–1M': 750_000,
  '1M–5M': 1_500_000,
  '1M+': 1_500_000,
  '5M+': 6_000_000,
};

/** The audience behind an account: every profile that has picked a band. Zero
 * when none has, which is what tells the card it has nothing to calculate. */
export function audienceOf(followerRanges: (string | null | undefined)[]): number {
  return followerRanges.reduce<number>(
    (sum, range) => sum + (range ? (FOLLOWER_MIDPOINTS[range] ?? 0) : 0),
    0,
  );
}

/**
 * Where a match stops being a maybe. The overview's confidence filter and the
 * exposure counts behind its estimate split on the same two numbers, because a
 * row a person read as "high" and an estimate that weighed it as "medium"
 * disagree about the same finding.
 */
export const CONFIDENCE_HIGH = 0.9;
export const CONFIDENCE_MEDIUM = 0.75;

/**
 * The detections the exposure factor is worked out from, counted by the
 * database rather than by a page holding ten rows.
 *
 * Split six ways because the two facts that change what a detection is worth
 * are how sure we are it is her and whether the page is still up. A gone page
 * is in none of these counts: material that is off the internet is not costing
 * her anything this month, which is the whole point of taking it down.
 */
export interface ExposureCounts {
  /** Still online, and the match is all but certain (confidence ≥ 0.9). */
  onlineHigh: number;
  /** Still online, a solid match (≥ 0.75). */
  onlineMedium: number;
  /** Still online, weaker or never compared against her own images. */
  onlineLow: number;
  /** Not known to be gone — blocked us, or never checked — by the same three. */
  unverifiedHigh: number;
  unverifiedMedium: number;
  unverifiedLow: number;
}

export const NO_EXPOSURE: ExposureCounts = {
  onlineHigh: 0,
  onlineMedium: 0,
  onlineLow: 0,
  unverifiedHigh: 0,
  unverifiedMedium: 0,
  unverifiedLow: 0,
};

/** What one detection of each kind adds. A page we watched go live under a
 * near-certain match is the unit; everything else is a fraction of it. */
const EXPOSURE_WEIGHTS: Record<keyof ExposureCounts, number> = {
  onlineHigh: 1,
  onlineMedium: 0.6,
  onlineLow: 0.25,
  unverifiedHigh: 0.4,
  unverifiedMedium: 0.25,
  unverifiedLow: 0.1,
};

/** The five bands, as a weighted-detection threshold and the share of income
 * that much exposure is taken to put at risk. Capped at 10% by the last row. */
const EXPOSURE_BANDS: { upTo: number; factor: number }[] = [
  { upTo: 0.5, factor: 0.02 },
  { upTo: 3, factor: 0.04 },
  { upTo: 8, factor: 0.06 },
  { upTo: 20, factor: 0.08 },
  { upTo: Infinity, factor: 0.1 },
];

/** How much of her material is out there, as one number. */
export function exposureWeight(counts: ExposureCounts): number {
  return (Object.keys(EXPOSURE_WEIGHTS) as (keyof ExposureCounts)[]).reduce(
    (sum, key) => sum + Math.max(0, counts[key] ?? 0) * EXPOSURE_WEIGHTS[key],
    0,
  );
}

/** 0.02–0.10. Never hardcoded per account: more detections, surer matches and
 * pages still up all push it towards the cap, and a takedown pulls it back. */
export function exposureFactor(counts: ExposureCounts): number {
  const weight = exposureWeight(counts);
  const band = EXPOSURE_BANDS.find((row) => weight < row.upTo) ?? EXPOSURE_BANDS[EXPOSURE_BANDS.length - 1];
  return Math.min(band.factor, 0.1);
}

/**
 * The share of an audience that pays, and what it pays, either side of the
 * range. Rough on purpose and the same two pairs for everybody: this is an
 * industry rule of thumb, not a measurement of her account.
 */
const CONVERSION_LOW = 0.015;
const CONVERSION_HIGH = 0.025;
const MONTHLY_SPEND_LOW = 20;
const MONTHLY_SPEND_HIGH = 24;

/** Whole dollars a month, low and high, for an audience and an exposure
 * factor. Zero audience gives zero, which the caller shows as sample data
 * rather than as "$0 at risk". */
export function revenueAtRisk(
  audience: number,
  factor: number,
): { monthlyLow: number; monthlyHigh: number } {
  const safeAudience = Number.isFinite(audience) ? Math.max(0, audience) : 0;
  const safeFactor = Number.isFinite(factor) ? Math.min(Math.max(factor, 0), 0.1) : 0;
  return {
    monthlyLow: Math.round(safeAudience * CONVERSION_LOW * MONTHLY_SPEND_LOW * safeFactor),
    monthlyHigh: Math.round(safeAudience * CONVERSION_HIGH * MONTHLY_SPEND_HIGH * safeFactor),
  };
}

/** Pages still up, by how sure the match is: the API and the admin preview count them alike. */
export function exposureOf(rows: Array<{ seen: 'live' | 'gone' | 'unverified'; confidence: number | null }>): ExposureCounts {
  const band = (confidence: number | null) =>
    confidence != null && confidence >= CONFIDENCE_HIGH ? 'High' : confidence != null && confidence >= CONFIDENCE_MEDIUM ? 'Medium' : 'Low';
  const out: ExposureCounts = { ...NO_EXPOSURE };
  for (const row of rows) {
    if (row.seen === 'gone') continue;
    out[`${row.seen === 'live' ? 'online' : 'unverified'}${band(row.confidence)}`]++;
  }
  return out;
}
